import { FC } from 'react';
import { Divider, Tooltip, Icon } from 'common-components';
import { capitalizeFirstLetter } from 'utils';
import { useTranslationContext } from '../../contexts/translationContext';
import Tag from '../../tag/tag';
import styles from './multiTagsSelect.module.scss';

const MultiTagsSelect: FC<MultiTagsSelectProps> = (props: MultiTagsSelectProps) => {
    const { tags1, onSelect, selectedTags = { tag1: '', tags2: [] } } = props;
    const { translate } = useTranslationContext();

    const tags2 = Object.values(tags1).reduce((accumulator, currentValue) => {
        return [...new Set([...accumulator, ...currentValue])];
    }, []);

    const onTag2Click = (tag: string, isChecked: boolean) => {
        let selectedTagsCpy = [...selectedTags.tags2];
        if (isChecked) {
            selectedTagsCpy = [...selectedTagsCpy, tag];
        } else {
            selectedTagsCpy.splice(selectedTagsCpy.indexOf(tag), 1);
        }
        onSelect(selectedTags.tag1, selectedTagsCpy);
    };

    const onTag1Click = (tag: string) => {
        const selectedTag1 = selectedTags.tag1 === tag ? '' : tag;
        const selectedTags2 = selectedTags.tags2.filter(t => tags1[tag]?.includes(t));
        onSelect(selectedTag1, selectedTags2);
    };

    const tooltip = (isDisabled: boolean) =>
        isDisabled ? (
            <div className={styles.tooltip}>
                <Icon name="warningYellow" className={styles.icon} />
                <div>
                    <div className={styles.title}>{translate('YouMayOnlySelectOneAtAtime')}</div>
                    <div className={styles.text}>{translate('UnselectThePreviousOptionToSelectThisOne')}</div>
                </div>
            </div>
        ) : (
            ''
        );

    return (
        <div>
            <div className={styles['tags-container']}>
                {Object.keys(tags1).map((tag, index) => {
                    const isDisabled = selectedTags.tag1 !== '' && selectedTags.tag1 !== tag;
                    return (
                        <Tooltip key={index} title={tooltip(isDisabled)}>
                            <span>
                                <Tag
                                    tagKey={tag}
                                    title={capitalizeFirstLetter(translate(tag))}
                                    isDisabled={
                                        isDisabled ||
                                        (!tags1[tag]?.some(t => selectedTags.tags2.includes(t)) &&
                                            selectedTags.tags2.length > 0)
                                    }
                                    onTagClick={onTag1Click}
                                    isCheckedCallback={selectedTags.tag1 === tag}
                                    isMarked={
                                        selectedTags.tags2.length > 0 &&
                                        tags1[tag]?.some(t => selectedTags.tags2.includes(t))
                                    }
                                />
                            </span>
                        </Tooltip>
                    );
                })}
            </div>
            <Divider />
            <div className={styles['tags-container']}>
                {tags2.map((tag, index) => {
                    return (
                        <Tag
                            key={index}
                            tagKey={tag}
                            title={capitalizeFirstLetter(translate(tag))}
                            onTagClick={(tagKey, isChecked) => onTag2Click(tagKey, isChecked)}
                            isCheckedCallback={selectedTags.tags2.includes(tag)}
                            isMarked={tags1[selectedTags.tag1]?.includes(tag)}
                            isDisabled={!tags1[selectedTags.tag1]?.includes(tag) && selectedTags.tag1 !== ''}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export interface MultiTagsSelectProps {
    tags1: Record<string, string[]>;
    onSelect: Function;
    selectedTags?: {
        tag1: string;
        tags2: string[];
    };
}

export default MultiTagsSelect;
